import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';

const routeNameMap = {
  dashboard: 'Dashboard',
  'identity-management': 'Identity Management',
  'manage-scans': 'Manage Scans',
  settings: 'Settings',
  repositories: 'Repositories',
  integration: 'Integration',
  iam: 'IAM',
  s3: 'S3',
  slack: 'Slack',
  teams: 'Teams',
  risk: 'Risk',
  onboarding: 'Onboarding',
  'blast-radius': 'Blast Radius',
  secrets: 'Secrets',
  channels: 'Channels',
  'cloud-iam': 'Cloud IAM',
  storage: 'Storage',
  'aws': 'AWS',
  'azure': 'Azure',
  'gcp': 'GCP',
  'github': 'GitHub',
  'gitlab': 'GitLab',
  'bitbucket': 'Bitbucket',
  'gitea': 'Gitea',
  'active-directory': 'Active Directory', // Added for this task
};

const formatSegment = (segment) => {
  if (routeNameMap[segment]) {
    return routeNameMap[segment];
  }
  // Fallback: replace hyphens with spaces and capitalize
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const DynamicBreadcrumbs = ({ findingProviderName }) => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  let breadcrumbsToRender = [{ name: 'Home', to: '/' }];

  // Check for the specific blast radius path pattern
  // Example: /identity-management/github/1083243170/blast-radius/89f80ec9-c209-4c33-83bd-c7b842856f91
  const isBlastRadiusPath = pathnames[0] === 'identity-management' &&
                            pathnames.length === 5 &&
                            pathnames[3] === 'blast-radius';

  if (isBlastRadiusPath) {
    const provider = pathnames[1]; // e.g., 'github'
    const integrationId = pathnames[2]; // e.g., '1083243170'
    const blastRadiusId = pathnames[4]; // e.g., '89f80ec9...'

    const providerName = routeNameMap[provider] || formatSegment(provider);

    // Retrieve the integration instance name from session storage
    const integrationCacheKey = `integration_name_${provider}_${integrationId}`;
    let integrationInstanceName = integrationId; // Fallback to ID
    try {
      const cached = sessionStorage.getItem(integrationCacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.name) {
          integrationInstanceName = parsed.name;
        }
      }
    } catch (e) {
      console.error("Failed to parse cached integration name", e);
    }

    breadcrumbsToRender.push({ name: 'Home', to: '/' });
    breadcrumbsToRender.push({ name: routeNameMap['identity-management'], to: '/identity-management' });
    breadcrumbsToRender.push({ name: providerName, to: `/identity-management/${provider}` }); // Link to integration list page
    breadcrumbsToRender.push({ name: integrationInstanceName, to: `/identity-management/${provider}/${integrationId}` }); // Link to integration instance page
    breadcrumbsToRender.push({ 
        name: findingProviderName || providerName, // This is the final breadcrumb (e.g., Postman)
        to: `/identity-management/${provider}/${integrationId}/blast-radius/${blastRadiusId}` // Link to the current blast radius page
    });

  } else {
    // Existing logic for other paths
    pathnames.forEach((value, index) => {
      if (value === 'iam') return;

      const to = `/${pathnames.slice(0, index + 1).join('/')}`;
      let formattedName = formatSegment(value);

      // Check session storage for friendly name (e.g. for integration IDs)
      if (index > 0) {
        const prevSegment = pathnames[index - 1];
        // Try to construct key based on integration pattern: integration_name_{type}_{id}
        const cacheKey = `integration_name_${prevSegment}_${value}`;
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && parsed.name) {
              formattedName = parsed.name;
            }
          }
        } catch (e) {
          // Ignore storage errors
        }
      }
      breadcrumbsToRender.push({ name: formattedName, to: to });
    });
  }


  return (
    <div className="mb-2">
      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbsToRender.map((item, index) => {
            const isLast = index === breadcrumbsToRender.length - 1;
            return (
              <React.Fragment key={item.to}>
                <BreadcrumbItem>
                  {isLast ? (
                    <BreadcrumbPage className="font-semibold">{item.name}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={item.to}>{item.name}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator />}
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
};

export default DynamicBreadcrumbs;